import type { Transition } from 'framer-motion'
import type { ReactNode } from 'react'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { useThemeEditorContext } from '../../../../hooks/useThemeEditorContext'

export type Position = 'top' | 'center' | 'bottom'

type Bounce = boolean | { options: Transition }

type BottomSheetContextType = {
  position: Position
  setPosition: (position: Position) => void
  shouldBounce: Bounce
  triggerBounce: (options?: Transition) => void
}

type BottomSheetContextProps = {
  children: ReactNode
}

const BottomSheetContext = createContext<BottomSheetContextType | undefined>(undefined)

export const BottomSheetContextProvider = ({ children }: BottomSheetContextProps) => {
  const { isPreviewLoading } = useThemeEditorContext()
  const [didBounceInitially, setDidBounceInitially] = useState<boolean>(false)
  const [position, setPosition] = useState<Position>('bottom')
  const [shouldBounce, setShouldBounce] = useState<Bounce>(false)

  const triggerBounce = (options?: Transition) => {
    setShouldBounce(options ? { options } : true)
    setTimeout(() => setShouldBounce(false), 0)
  }

  useEffect(() => {
    if (!didBounceInitially && !isPreviewLoading && position === 'bottom') {
      triggerBounce({ delay: 1.5 })
      setDidBounceInitially(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviewLoading])

  const value = useMemo(
    () => ({
      position,
      setPosition,
      shouldBounce,
      triggerBounce,
    }),
    [shouldBounce, position],
  )

  return <BottomSheetContext.Provider value={value}>{children}</BottomSheetContext.Provider>
}

export const useBottomSheetContext = () => {
  const context = useContext(BottomSheetContext)

  if (context === undefined) {
    throw new Error('useBottomSheetContext must be used within an BottomSheetProvider')
  }

  return context
}
